import { Client, Server, ServerRequest, AuthUser } from '../types';

const STORAGE_KEYS = {
  CLIENTS: 'lisa_server_clients',
  SERVERS: 'lisa_server_servers',
  SERVER_REQUESTS: 'lisa_server_requests',
  AUTH_USER: 'lisa_server_auth_user',
};

// Genera un hash único de 8 caracteres
export const generateClientHash = (): string => {
  return Math.random().toString(36).substring(2, 10);
};

// --- Funciones de clientes ---
const initialClients: Client[] = [
  {
    id: 'test-client-1',
    hash: 'k7lv99vr',
    username: 'testuser',
    password: 'test123',
    email: 'test@example.com',
    name: 'Test Company',
    createdAt: new Date().toISOString(),
  }
];

export const getClients = (): Client[] => {
  const stored = localStorage.getItem(STORAGE_KEYS.CLIENTS);
  if (stored) {
    const clients = JSON.parse(stored);
    return clients;
  }
  
  saveClients(initialClients);
  return initialClients;
};

export const saveClients = (clients: Client[]): void => {
  localStorage.setItem(STORAGE_KEYS.CLIENTS, JSON.stringify(clients));
};

export const addClient = (client: Omit<Client, 'id' | 'hash' | 'createdAt'>): Client => {
  const clients = getClients();
  const newClient: Client = {
    ...client,
    id: Date.now().toString(),
    hash: generateClientHash(),
    createdAt: new Date().toISOString(),
  };
  
  clients.push(newClient);
  saveClients(clients);
  return newClient;
};

export const deleteClient = (clientId: string): void => {
  const clients = getClients().filter(client => client.id !== clientId);
  saveClients(clients);
  
  const servers = getServers().map(server => 
    server.assignedTo === clientId 
      ? { ...server, assignedTo: undefined, status: 'offline' as const }
      : server
  );
  saveServers(servers);
};

export const getClientByHash = (hash: string): Client | undefined => {
  return getClients().find(client => client.hash === hash);
};


// --- Funciones de servidores ---
const initialServers: Server[] = [
  {
    id: 'server-1',
    name: 'SERVER-19521',
    processor: 'Intel Xeon E5-2680v4',
    ram: '32GB DDR4',
    hd: '1TB NVMe SSD',
    connection: '1Gbps',
    price: '$89/mes',
    location: 'Datacenter',
    status: 'online', 
    assignedTo: 'test-client-1',
    ip: '62.45.122.100',
    sshUser: 'root',
    sshPass: 'root',
    royaleServiceId: '19521',
  },
  {
    id: 'server-2',
    name: 'SERVER-19522',
    processor: 'Intel Xeon E5-2680v4',
    ram: '32GB DDR4',
    hd: '1TB NVMe SSD',
    connection: '1Gbps',
    price: '$89/mes',
    location: 'Datacenter',
    status: 'offline',
    assignedTo: 'test-client-1',
    ip: '144.122.15.42',
    sshUser: 'root',
    sshPass: 'root',
    royaleServiceId: '19522',
  }
];

export const getServers = (): Server[] => {
  const stored = localStorage.getItem(STORAGE_KEYS.SERVERS);
  if (stored) {
    const servers = JSON.parse(stored);
    return servers;
  }
  
  saveServers(initialServers);
  return initialServers;
};

export const saveServers = (servers: Server[]): void => {
  localStorage.setItem(STORAGE_KEYS.SERVERS, JSON.stringify(servers));
};

export const addServer = (server: Omit<Server, 'id' | 'status'>): Server => {
  const servers = getServers();
  const newServer: Server = {
    ...server,
    id: Date.now().toString(),
    status: 'offline',
  };
  
  servers.push(newServer);
  saveServers(servers);
  return newServer;
};

export const deleteServer = (serverId: string): void => {
  const servers = getServers().filter(server => server.id !== serverId);
  saveServers(servers);
};

export const updateServerStatus = (serverId: string, newStatus: 'online' | 'offline' | 'maintenance'): void => {
  const servers = getServers();
  const updatedServers = servers.map(server =>
    server.id === serverId ? { ...server, status: newStatus } : server
  );
  saveServers(updatedServers);
};

export const assignServerToClient = (serverId: string, clientId: string): void => {
  const servers = getServers().map(server =>
    server.id === serverId ? { ...server, assignedTo: clientId } : server
  );
  saveServers(servers);
};

export const unassignServerFromClient = (serverId: string): void => {
  const servers = getServers();
  const updatedServers = servers.map(s =>
    s.id === serverId ? { ...s, assignedTo: undefined } : s
  );
  saveServers(updatedServers);
};

export const getServerRequests = (): ServerRequest[] => {
  const stored = localStorage.getItem(STORAGE_KEYS.SERVER_REQUESTS);
  return stored ? JSON.parse(stored) : [];
};

export const saveServerRequests = (requests: ServerRequest[]): void => {
  localStorage.setItem(STORAGE_KEYS.SERVER_REQUESTS, JSON.stringify(requests));
};

export const addServerRequest = (request: Omit<ServerRequest, 'id' | 'requestedAt'>): void => {
  const requests = getServerRequests();
  const newRequest: ServerRequest = {
    ...request,
    id: Date.now().toString(),
    requestedAt: new Date().toISOString(),
  };
  
  requests.push(newRequest);
  saveServerRequests(requests);
};

export const getAuthUser = (): AuthUser | null => {
  const stored = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
  return stored ? JSON.parse(stored) : null;
};

export const setAuthUser = (user: AuthUser | null): void => {
  if (user) {
    localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
  }
};

// Simulación de API de Royale Hosting
export const royaleHostingAPI = {
  startServer: async (serverId: string): Promise<boolean> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const servers = getServers();
    const updatedServers = servers.map(server =>
      server.id === serverId ? { ...server, status: 'online' as const } : server
    );
    saveServers(updatedServers);
    return true;
  },
  
  stopServer: async (serverId: string): Promise<boolean> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const servers = getServers();
    const updatedServers = servers.map(server =>
      server.id === serverId ? { ...server, status: 'offline' as const } : server
    );
    saveServers(updatedServers);
    return true;
  },
  
  restartServer: async (serverId: string): Promise<boolean> => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    const servers = getServers();
    const updatedServers = servers.map(server =>
      server.id === serverId ? { ...server, status: 'online' as const } : server
    );
    saveServers(updatedServers);
    return true;
  },
};

export const clearAllData = () => {
  localStorage.removeItem(STORAGE_KEYS.CLIENTS);
  localStorage.removeItem(STORAGE_KEYS.SERVERS);
  localStorage.removeItem(STORAGE_KEYS.SERVER_REQUESTS);
  localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
  window.location.reload();
};
